import React, { useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import { DashboardCanvas } from '../components/DashboardCanvas'
import { DashboardControls } from '../components/DashboardControls'
import { DashboardProvider, DEFAULT_TIME_RANGES, type TimeRange } from '../contexts/DashboardContext'
import {
  Container,
  Header,
  Button,
  Box,
  Spinner,
  Alert,
  SpaceBetween
} from '@cloudscape-design/components'
import { Id } from '../../../../convex/_generated/dataModel'

// Helper function to convert string time range to TimeRange object
const convertStringToTimeRange = (timeRangeString: string): TimeRange => {
  // Map common string values to TimeRange objects
  const timeRangeMap: Record<string, TimeRange> = {
    // Full format
    'last-5-minutes': DEFAULT_TIME_RANGES[0],
    'last-15-minutes': DEFAULT_TIME_RANGES[1], 
    'last-30-minutes': DEFAULT_TIME_RANGES[2],
    'last-1-hour': DEFAULT_TIME_RANGES[3],
    'last-6-hours': DEFAULT_TIME_RANGES[4],
    'last-12-hours': DEFAULT_TIME_RANGES[5],
    'last-24-hours': DEFAULT_TIME_RANGES[6],
    'last-7-days': DEFAULT_TIME_RANGES[7],
    'last-30-days': DEFAULT_TIME_RANGES[8],
    // Short format (stored in database)
    '5m': DEFAULT_TIME_RANGES[0],
    '15m': DEFAULT_TIME_RANGES[1],
    '30m': DEFAULT_TIME_RANGES[2],
    '1h': DEFAULT_TIME_RANGES[3],
    '6h': DEFAULT_TIME_RANGES[4],
    '12h': DEFAULT_TIME_RANGES[5],
    '24h': DEFAULT_TIME_RANGES[6],
    '7d': DEFAULT_TIME_RANGES[7],
    '30d': DEFAULT_TIME_RANGES[8]
  }
  
  // Silently convert time range
  return timeRangeMap[timeRangeString] || DEFAULT_TIME_RANGES[3] // Default to last 1 hour
}

export const DashboardViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { sessionId } = useAmplifyAuth()
  
  // Fetch dashboard
  const dashboard = useQuery(
    id && sessionId ? api.components.dashboards.index.getDashboard : "skip",
    id && sessionId ? { sessionId, dashboardId: id as Id<"dashboards"> } : "skip"
  )

  // Set page header with dashboard name for breadcrumbs
  const { setHeaderConfig } = usePageHeader()
  
  useEffect(() => {
    if (dashboard) {
      setHeaderConfig({
        title: dashboard.name,
        description: dashboard.description || '',
        actions: []
      })
    }
  }, [dashboard])


  // Show loading state
  if (dashboard === undefined) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <div>Loading dashboard...</div>
      </Box>
    )
  }

  // Show not found state
  if (dashboard === null) {
    return (
      <Alert
        statusIconAriaLabel="Error"
        type="error"
        header="Dashboard not found"
        action={
          <Button onClick={() => navigate('/dashboards')}>
            Back to Dashboards
          </Button>
        }
      >
        The requested dashboard could not be found or you don't have access to it.
      </Alert>
    )
  }

  const handleDashboardChange = (updatedDashboard: any) => {
    // In viewer mode, changes are not saved automatically
  }

  return (
    <DashboardProvider 
      dashboard={dashboard} 
      initialTimeRange={convertStringToTimeRange(dashboard.default_time_range)}
    >
      <div style={{ padding: '8px' }}>
        {/* Compact Header with Controls in One Row */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px',
          padding: '8px 16px',
          backgroundColor: '#fafbfc',
          borderRadius: '8px',
          border: '1px solid #e9ebed'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div>
              <h1 style={{ margin: '0', fontSize: '20px', fontWeight: '500' }}>
                {dashboard.name}
              </h1>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DashboardControls />
            </div>
          </div>
          <Button 
            variant="icon"
            iconName="edit"
            onClick={() => navigate(`/dashboards/${id}/edit`)}
            ariaLabel="Edit dashboard"
          />
        </div>

        {/* Dashboard Canvas with Real Widgets */}
        <DashboardCanvas
          dashboard={dashboard}
          onChange={handleDashboardChange}
          isEditing={false}
        />

        {/* Dashboard Metadata */}
        <Box margin={{ top: 'm' }}>
          <Container>
            <div style={{ 
              fontSize: '12px', 
              color: '#879596',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                Last updated: {dashboard._creationTime ? new Date(dashboard._creationTime).toLocaleString() : 'Unknown'}
              </div>
              <div>
                Refresh interval: {dashboard.refresh_interval} seconds
              </div>
              <div>
                {dashboard.widgets?.length || 0} widget{(dashboard.widgets?.length || 0) !== 1 ? 's' : ''}
              </div>
            </div>
          </Container>
        </Box>
      </div>
    </DashboardProvider>
  )
}